/**
 * Aufgaben-Utilities — Hilfsfunktionen für das Aufgaben-Modul
 *
 * - Datum/Uhrzeit Konvertierung für datetime-local Inputs
 * - Datei-Upload Validierung
 */

/** Max. Dateigröße in Bytes (50 MB) */
export const MAX_FILE_SIZE = 50 * 1024 * 1024;

/**
 * Formatiert einen faellig_am Wert (ISO-String oder Datum-String) für ein datetime-local Input.
 *
 * WICHTIG: Date-only Strings ("2026-04-15") werden als lokale Mitternacht interpretiert,
 * NICHT als UTC-Mitternacht. Das verhindert Zeitverschiebungen durch Timezone-Offset.
 */
export function formatDatetimeLocalValue(faelligAm: string | null | undefined): string {
  if (!faelligAm) return '';

  // Date-only String erkennen (YYYY-MM-DD, kein T)
  if (/^\d{4}-\d{2}-\d{2}$/.test(faelligAm)) {
    return `${faelligAm}T00:00`;
  }

  const d = new Date(faelligAm);
  if (isNaN(d.getTime())) return '';

  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Konvertiert einen datetime-local Input-Wert zu einem ISO-String für die DB.
 * Der Input-Wert ist im Format "YYYY-MM-DDTHH:MM" und repräsentiert lokale Zeit.
 */
export function parseDatetimeLocalToISO(value: string): string | null {
  if (!value) return null;
  // datetime-local Wert ist lokale Zeit — Date-Konstruktor interpretiert es als lokal
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

/**
 * Validiert eine Datei vor dem Upload.
 * Akzeptiert ALLE Dateiformate (inkl. ZIP, Office, Medien etc.).
 * Begrenzt nur die Dateigröße auf MAX_FILE_SIZE.
 */
export function validateFileUpload(file: File): { valid: boolean; error?: string } {
  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = Math.round(file.size / (1024 * 1024));
    return {
      valid: false,
      error: `Datei zu groß (${sizeMB} MB). Maximal ${MAX_FILE_SIZE / (1024 * 1024)} MB erlaubt.`,
    };
  }

  return { valid: true };
}
